import { RealtimeClient } from "./realtime-client.js";

const key = ({ channel, resourceId }) => `${channel}:${resourceId}`;
const accepts = (lease, frame) => lease.topics.some(t => t.channel === frame.channel && (t.resourceId === frame.resourceId || t.resourceId === "self"));

/** 同一登录身份、同一页面只建立一个物理连接；组件只拥有可释放的订阅。 */
export class SharedRealtimeHub {
  constructor(token, dependencies = {}) {
    this.leases = new Set(); this.references = new Map(); this.ready = new Set(); this.state = "CONNECTING";
    this.failed = new Set();
    this.client = new RealtimeClient({ ...dependencies, token,
      onEvent: frame => this.dispatch("onEvent", frame),
      onError: error => this.leases.forEach(l => l.onError?.(error)),
      onResourceError: error => {
        const topic = key(error); this.ready.delete(topic); this.failed.add(topic);
        this.leases.forEach(l => {
          if (!l.topics.some(t => key(t) === topic)) return;
          l.blocked.add(topic); l.onError?.(error);
          if (l.topics.every(t => l.blocked.has(key(t)))) { l.connected = false; l.onState?.("REJECTED"); }
          else if (l.topics.every(t => l.blocked.has(key(t)) || this.ready.has(key(t)))) { l.connected = true; l.onState?.("CONNECTED"); this.deliver(l, "onReady", error); }
        });
      },
      onReady: frame => {
        this.ready.add(key(frame));
        this.leases.forEach(l => {
          l.blocked.delete(key(frame));
          if (!l.connected && l.topics.some(t => !l.blocked.has(key(t))) && l.topics.every(t => this.ready.has(key(t)) || l.blocked.has(key(t)))) {
            l.connected = true; l.onState?.("CONNECTED"); this.deliver(l, "onReady", frame);
          }
        });
      },
      onState: state => {
        this.state = state;
        if (state === "CONNECTED") return; // 按各组件所需资源确认，而非任一订阅成功即算就绪。
        this.ready.clear();
        this.leases.forEach(l => { l.connected = false; l.onState?.(state); });
      },
    });
  }
  deliver(lease, method, value) {
    const epoch = this.client.epoch;
    Promise.resolve().then(() => {
      if (epoch === this.client.epoch && this.leases.has(lease)) return lease[method]?.(value);
    }).catch(error => { if (this.leases.has(lease)) lease.onError?.(error); });
  }
  dispatch(method, frame) {
    this.leases.forEach(l => { if (accepts(l, frame)) this.deliver(l, method, frame); });
  }
  acquire(options) {
    const lease = { ...options, topics: options.subscriptions, connected: false, blocked: new Set() };
    this.leases.add(lease);
    for (const topic of lease.topics) {
      const id = key(topic), count = this.references.get(id) || 0;
      this.references.set(id, count + 1);
      if (!count || this.failed.delete(id)) this.client.subscribe(topic.channel, topic.resourceId);
    }
    this.client.connect();
    if (lease.topics.every(t => this.ready.has(key(t)))) {
      lease.connected = true; lease.onState?.("CONNECTED"); this.deliver(lease, "onReady", lease.topics[0]);
    } else lease.onState?.(this.state === "CONNECTED" ? "CONNECTING" : this.state);
    return { close: () => {
      if (!this.leases.delete(lease)) return;
      for (const topic of lease.topics) {
        const id = key(topic), count = this.references.get(id) - 1;
        if (count) this.references.set(id, count);
        else { this.references.delete(id); this.ready.delete(id); this.client.unsubscribe(topic.channel, topic.resourceId); }
      }
      if (!this.leases.size) this.client.close();
    } };
  }
  close() { this.client.close(); this.leases.clear(); this.references.clear(); this.ready.clear(); }
}

let current = null;
/** 切换身份先撤销旧连接；Token只保存在会话内存及AUTH帧。 */
export function acquireRealtime(options) {
  if (!options.token) return { close() {} };
  if (!current || current.client.token !== options.token || current.client.closed) {
    current?.close(); current = new SharedRealtimeHub(options.token, options);
  }
  return current.acquire(options);
}
/** 鉴权恢复事件回到同一分发层；旧身份及关闭连接的响应不能污染新页面。 */
export function emitRecoveredRealtime(token, event) {
  if (current && !current.client.closed && current.client.token === token) current.dispatch("onEvent", event);
}
